import { useState, useEffect, useRef } from 'react'

import close from 'assets/icons/close.svg'
import HistoryIcon from 'assets/icons/history.svg'
import SearchIcon from 'assets/icons/SearchIcon.svg'

import { highlightMatch } from './highlightMatch'
import { useBrandRecommendations } from '../../hooks/useBrandReccomendations'
import { useDebounce } from '../../hooks/useDebounce'

import './brandTable.scss'
interface Props {
  value: string
  onChange: (v: string) => void
  onSearch: (v: string) => void
}

export const BrandSearch = ({ value, onChange, onSearch }: Props) => {
  const debouncedSearch = useDebounce(value, 400)

  const { data: recommendations = [] } = useBrandRecommendations(debouncedSearch)

  const [history, setHistory] = useState<string[]>([])
  const [activeIndex, setActiveIndex] = useState(-1)
  const [showHistory, setShowHistory] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  const wrapperRef = useRef<HTMLDivElement>(null)

  /* ---------------- FILTER RECOMMENDATIONS ---------------- */

  const search = value.toLowerCase()

  const filteredRecommendations = recommendations.filter(
    (item: any) =>
      item?.brandName?.toLowerCase().includes(search) ||
      item?.version?.toString().toLowerCase().includes(search) ||
      item?.category?.toLowerCase().includes(search) ||
      item?.owner?.toLowerCase().includes(search),
  )

  const getMatchedValue = (item: any, search: string) => {
    const query = search.toLowerCase()

    if (item?.brandName?.toLowerCase().includes(query)) return item.brandName

    if (item?.version?.toString().toLowerCase().includes(query)) return item.version.toString()

    if (item?.category?.toLowerCase().includes(query)) return item.category

    if (item?.owner?.toLowerCase().includes(query)) return item.owner

    return item.brandName
  }
  const uniqueRecommendations = Array.from(
    new Map(
      filteredRecommendations.map((item: any) => {
        const matchedValue = getMatchedValue(item, value)
        return [matchedValue, matchedValue]
      }),
    ).values(),
  )
  /* ---------------- OUTSIDE CLICK ---------------- */

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
        setShowHistory(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  /* ---------------- LOAD HISTORY ---------------- */

  useEffect(() => {
    const stored = localStorage.getItem('brandSearchHistory')
    if (stored) setHistory(JSON.parse(stored))
  }, [])

  /* ---------------- SAVE HISTORY ---------------- */

  const saveHistory = (term: string) => {
    if (!term.trim()) return

    let updated = [term, ...history.filter(h => h !== term)]

    updated = updated.slice(0, 3)

    setHistory(updated)

    localStorage.setItem('brandSearchHistory', JSON.stringify(updated))
  }

  /* ---------------- SELECT SEARCH ---------------- */

  const handleSelect = (term: string) => {
    onChange(term)
    onSearch(term)
    saveHistory(term)

    setShowDropdown(false)
    setShowHistory(false)
  }

  /* ---------------- CLEAR INPUT ---------------- */

  const clearInput = () => {
    onChange('')
    onSearch('')
    setActiveIndex(-1)

    if (history.length > 0) {
      setShowHistory(true)
    }
  }

  /* ---------------- KEYBOARD NAV ---------------- */

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && filteredRecommendations.length) {
      setActiveIndex(i => (i < filteredRecommendations.length - 1 ? i + 1 : i))
    }

    if (e.key === 'ArrowUp' && filteredRecommendations.length) {
      setActiveIndex(i => (i > 0 ? i - 1 : 0))
    }

    if (e.key === 'Enter') {
      if (activeIndex >= 0 && filteredRecommendations.length) {
        handleSelect(filteredRecommendations[activeIndex].brandName)
      } else {
        onSearch(value)
        saveHistory(value)

        setShowDropdown(false)
        setShowHistory(false)
      }
    }
  }

  /* ---------------- INPUT CHANGE ---------------- */

  const handleChange = (text: string) => {
    onChange(text)

    setActiveIndex(-1)

    if (text.length === 0 && history.length > 0) {
      setShowHistory(true)
    } else {
      setShowHistory(false)
    }

    if (text.length >= 3) {
      setShowDropdown(true)
    } else {
      setShowDropdown(false)
    }
  }

  /* ---------------- COMPONENT ---------------- */

  return (
    <div className="relative" ref={wrapperRef}>
      {/* INPUT */}

      <div className="flex items-center search-wrapper">
        <img src={SearchIcon} alt="" width="20" height="20" />

        <input
          className="outline-none body-s color-neutral-70 search-border"
          placeholder="Search..."
          value={value}
          onChange={e => handleChange(e.target.value)}
          onFocus={() => {
            if (!value && history.length > 0) {
              setShowHistory(true)
            }
          }}
          onKeyDown={handleKeyDown}
        />

        {/* {value && ( */}
        <div className="min-w-[16px]">
          <button onClick={clearInput} className={`${value ? '' : 'hidden'}`}>
            <img src={close} alt="close" width="16" height="16" />
          </button>
        </div>
        {/* )} */}
      </div>

      {/* ---------------- HISTORY ---------------- */}

      {showHistory && history.length > 0 && (
        <div className="absolute history-bg border w-full mt-1 p-4 rounded shadow">
          {history.map((item, index) => (
            <div
              key={index}
              className="flex items-center gap-[6px] color-neutral-80 body-s cursor-pointer"
              onClick={() => handleSelect(item)}
            >
              <img src={HistoryIcon} alt="" width="14" />

              {item}
            </div>
          ))}
        </div>
      )}

      {/* ---------------- RECOMMENDATIONS ---------------- */}

      {showDropdown && filteredRecommendations.length > 0 && value.length >= 3 && (
        <div className="absolute reccomend-bg border w-full mt-1 shadow">
          <div className="subtitle-xs">RECOMMENDED</div>

          {uniqueRecommendations.slice(0, 3).map((item: any, index: number) => (
            <div
              key={index}
              className={`body-s color-neutral-80 cursor-pointer ${index === activeIndex ? 'bg-gray-100' : ''}`}
              onClick={() => handleSelect(item)}
            >
              {highlightMatch(item ?? '', value ?? '')}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
